import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Activity, GitBranch, Clock, AlertTriangle, CheckCircle, XCircle, ExternalLink, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function ConfidenceRing({ score }) {
  const pct = Math.round((score || 0) * 100);
  const r = 36, c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const color = pct >= 90 ? "#EF4444" : pct >= 70 ? "#F59E0B" : "#3B82F6";
  return (
    <div className="relative w-24 h-24 flex items-center justify-center">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#E5E7EB" strokeWidth="5" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 1.5s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black" style={{ color }}>{pct}</span>
        <span className="text-[8px] uppercase tracking-widest text-muted-foreground font-bold">Confidence</span>
      </div>
    </div>
  );
}

export default function IncidentReportPage() {
  const { incidentId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/incidents/${incidentId}/public`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => { setError(e.response?.status === 404 ? "Incident not found" : "Failed to load report"); setLoading(false); });
  }, [incidentId]);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">Loading Incident Report</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center max-w-sm">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-black mb-2">{error}</h2>
        <p className="text-sm text-muted-foreground mb-4">This incident report may have been removed or doesn't exist.</p>
        <Link to="/login" className="text-sm text-primary hover:underline font-medium">Go to Pipeline Doctor</Link>
      </div>
    </div>
  );

  const sevColor = { critical: "text-red-600", high: "text-amber-600", medium: "text-yellow-600", low: "text-blue-600" };
  const statColor = { open: "text-red-600", acknowledged: "text-amber-600", resolved: "text-emerald-600" };

  return (
    <div className="min-h-screen bg-background" data-testid="incident-report-page">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-sm flex items-center justify-center bg-gradient-to-br from-blue-600 to-indigo-700">
              <Activity className="w-4 h-4 text-[#2D3142]" />
            </div>
            <div>
              <h1 className="text-sm font-black tracking-tight">PIPELINE DOCTOR</h1>
              <p className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground font-bold">Incident Report</p>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px] font-mono">{incidentId}</Badge>
        </div>
      </header>

      {/* Report Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Title Section */}
        <div className="flex items-start gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className={`status-dot ${data.severity}`} />
              <Badge variant={data.severity === "critical" ? "destructive" : "outline"} className="text-[10px] uppercase font-bold">{data.severity}</Badge>
              <Badge variant="outline" className={`text-[10px] uppercase ${statColor[data.status]}`}>{data.status}</Badge>
              <Badge variant="outline" className="text-[10px] uppercase font-mono">{data.category?.replace(/_/g, " ")}</Badge>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-3">{data.title}</h2>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5 font-mono"><Clock className="w-3.5 h-3.5" />{data.created_at?.slice(0, 16).replace("T", " ")}</span>
              {data.pipeline && <span className="flex items-center gap-1.5">Pipeline: <span className="font-mono font-medium text-foreground">{data.pipeline.name}</span></span>}
              {data.resolved_at && <span className="flex items-center gap-1.5 text-emerald-600"><CheckCircle className="w-3.5 h-3.5" />Resolved {data.resolved_at?.slice(0, 16).replace("T", " ")}</span>}
            </div>
          </div>
          {data.confidence_score != null && <ConfidenceRing score={data.confidence_score} />}
        </div>

        <Separator />

        {/* Pipeline Info */}
        {data.pipeline && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Source", value: data.pipeline.source_system },
              { label: "Destination", value: data.pipeline.destination_asset },
              { label: "SLA", value: `${data.pipeline.sla_minutes} min` },
              { label: "Current Lag", value: `${data.pipeline.freshness_lag_minutes} min`, warn: data.pipeline.freshness_lag_minutes > data.pipeline.sla_minutes },
            ].map((item) => (
              <div key={item.label} className="bg-card border border-border p-3 rounded-sm">
                <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{item.label}</p>
                <p className={`text-sm font-mono font-medium ${item.warn ? "text-red-600" : ""}`}>{item.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        <div>
          <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Summary</h3>
          <p className="text-base leading-relaxed">{data.summary}</p>
        </div>

        {/* AI Summary */}
        {data.ai_summary && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/20 border border-blue-200/50 dark:border-blue-800/30 rounded-sm p-4">
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-blue-700 dark:text-blue-400 mb-2">AI Analysis</h3>
            <p className="text-sm leading-relaxed">{data.ai_summary}</p>
          </div>
        )}

        {/* Root Cause */}
        <div>
          <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Root Cause Hypothesis</h3>
          <p className="text-base leading-relaxed">{data.root_cause_hypothesis || "Under investigation"}</p>
        </div>

        {/* Blast Radius */}
        {data.blast_radius?.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2 flex items-center gap-1.5"><GitBranch className="w-3.5 h-3.5" /> Blast Radius ({data.blast_radius.length} downstream assets)</h3>
            <div className="flex flex-wrap gap-2">
              {data.blast_radius.map((a) => (
                <div key={a} className="flex items-center gap-1.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 px-3 py-1.5 rounded-sm">
                  <AlertTriangle className="w-3 h-3 text-red-500" />
                  <span className="text-sm font-mono font-medium">{a}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Remediations */}
        {data.remediations?.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Remediation Actions</h3>
            <div className="space-y-2">
              {data.remediations.map((rem) => (
                <div key={rem.action_id} className="border border-border rounded-sm p-3 bg-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] uppercase font-mono">{rem.action_type?.replace(/_/g, " ")}</Badge>
                    <Badge variant={rem.status === "approved" ? "secondary" : rem.status === "rejected" ? "destructive" : "outline"} className="text-[10px] uppercase">{rem.status}</Badge>
                    {rem.approved_by && <span className="text-[10px] text-muted-foreground font-mono ml-auto">by {rem.approved_by}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">{rem.recommendation}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        {data.timeline?.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Incident Timeline</h3>
            <div className="space-y-0">
              {data.timeline.map((event, i) => (
                <div key={event.audit_id} className="flex items-start gap-3 pb-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${event.action === "resolved" || event.action === "approved" ? "bg-emerald-500" : event.action === "rejected" ? "bg-red-500" : "bg-blue-500"}`} />
                    {i < data.timeline.length - 1 && <div className="w-px h-full bg-border min-h-[20px]" />}
                  </div>
                  <div className="pb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px] uppercase font-bold">{event.action}</Badge>
                      <span className="text-[10px] text-muted-foreground font-mono">{event.created_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{event.detail}</p>
                    <p className="text-[10px] text-muted-foreground font-mono">by {event.actor}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Separator />

        <div className="text-center py-4">
          <p className="text-xs text-muted-foreground mb-2">This report is generated by Pipeline Doctor - Data Reliability Control Tower</p>
          <Link to="/login" className="text-xs text-primary hover:underline font-medium flex items-center justify-center gap-1">
            Open Pipeline Doctor <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
